import { motion } from 'framer-motion';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle } from 'lucide-react';

const faqCategories = [
  {
    title: 'Ingressi e Prenotazioni',
    items: [
      {
        question: 'Come posso prenotare un ingresso?',
        answer: 'Puoi prenotare direttamente online dalla sezione Abbonamenti, oppure contattarci via WhatsApp o telefono. La prenotazione è consigliata, specialmente nei weekend e nei giorni festivi.',
      },
      {
        question: 'È necessario prenotare?',
        answer: 'La prenotazione non è obbligatoria ma è fortemente consigliata, soprattutto nei weekend. Abbiamo una capienza limitata per garantire un\'esperienza esclusiva a tutti i nostri ospiti.',
      },
      {
        question: 'Cosa è incluso nell\'ingresso?',
        answer: 'L\'ingresso giornaliero include: lettino, ombrellone, accesso alla piscina, spogliatoi con docce e armadietti. Bar e ristorante sono a pagamento separato.',
      },
      {
        question: 'Posso acquistare un abbonamento stagionale?',
        answer: 'Attualmente offriamo ingressi giornalieri e il Young Pass per le serate. Per informazioni su abbonamenti stagionali personalizzati, contattaci direttamente.',
      },
    ],
  },
  {
    title: 'Meteo e Rimborsi',
    items: [
      {
        question: 'Cosa succede in caso di maltempo?',
        answer: 'In caso di maltempo, gli ingressi prenotati possono essere riprogrammati senza costi aggiuntivi. Contattaci almeno 24 ore prima per modificare la prenotazione.',
      },
      {
        question: 'Posso ottenere un rimborso?',
        answer: 'Offriamo rimborso completo per cancellazioni effettuate almeno 48 ore prima. Per cancellazioni entro le 24 ore, offriamo un voucher del valore pari all\'importo pagato.',
      },
      {
        question: 'Come funziona la garanzia meteo?',
        answer: 'Se le condizioni meteo non permettono l\'utilizzo della piscina, riceverai automaticamente un voucher per una data alternativa. Verifica sempre le previsioni prima di venire.',
      },
    ],
  },
  {
    title: 'Regole Piscina',
    items: [
      {
        question: 'Qual è il dress code?',
        answer: 'Durante il giorno: costume da bagno elegante, pareo o copricostume. Evitare abbigliamento sportivo. Per le serate: smart casual, no sportswear.',
      },
      {
        question: 'Ci sono limiti di età?',
        answer: 'Per la piscina diurna accettiamo ospiti dai 13 anni in su, se accompagnati da un adulto. Per gli eventi serali, il limite di età varia in base al tipo di evento: dagli eventi 14+ fino agli eventi riservati ai 21+. Verifica sempre i requisiti specifici dell\'evento a cui vuoi partecipare.',
      },
      {
        question: 'Posso portare cibo e bevande dall\'esterno?',
        answer: 'No, non è consentito introdurre cibo e bevande dall\'esterno. Abbiamo un bar e un servizio ristorante con un\'ampia selezione di piatti e cocktail.',
      },
      {
        question: 'È consentito fumare?',
        answer: 'È vietato fumare a bordo piscina e nelle aree comuni. Sono disponibili aree dedicate ai fumatori.',
      },
      {
        question: 'Sono ammessi animali?',
        answer: 'Purtroppo gli animali domestici non sono ammessi nelle aree della piscina per motivi igienico-sanitari.',
      },
    ],
  },
  {
    title: 'Oggetti Smarriti',
    items: [
      {
        question: 'Ho perso un oggetto, cosa devo fare?',
        answer: 'Contattaci via WhatsApp o email descrivendo l\'oggetto smarrito e la data della tua visita. Conserviamo gli oggetti ritrovati per 30 giorni.',
      },
      {
        question: 'Avete armadietti con chiave?',
        answer: 'Sì, offriamo armadietti con chiave negli spogliatoi. Ti consigliamo di non lasciare oggetti di valore incustoditi sui lettini.',
      },
    ],
  },
  {
    title: 'Eventi Privati',
    items: [
      {
        question: 'Come posso organizzare un evento privato?',
        answer: 'Compila il form nella sezione Eventi Privati con le tue preferenze. Ti contatteremo entro 24 ore per discutere i dettagli e inviarti un preventivo personalizzato.',
      },
      {
        question: 'Qual è la capienza massima per eventi?',
        answer: 'La capienza varia in base al tipo di evento. Per feste private fino a 200 persone, per eventi esclusivi con affitto totale fino a 300 persone.',
      },
      {
        question: 'Quanto tempo prima devo prenotare?',
        answer: 'Consigliamo di prenotare con almeno 2-4 settimane di anticipo. Per date molto richieste (weekend estivi, festività) suggeriamo 1-2 mesi.',
      },
      {
        question: 'Offrite servizio catering?',
        answer: 'Sì, abbiamo partner selezionati per catering di ogni tipo: aperitivo, cena seduta, buffet, cocktail party. Possiamo personalizzare il menu in base alle tue esigenze.',
      },
    ],
  },
  {
    title: 'Shop: Spedizioni e Resi',
    items: [
      {
        question: 'Quali sono i tempi di spedizione?',
        answer: 'Le spedizioni vengono effettuate entro 24-48 ore dall\'ordine. La consegna avviene in 3-5 giorni lavorativi in tutta Italia.',
      },
      {
        question: 'La spedizione è gratuita?',
        answer: 'La spedizione è gratuita per ordini superiori a €100. Per ordini inferiori, il costo è di €5.90.',
      },
      {
        question: 'Come funzionano i resi?',
        answer: 'Accettiamo resi entro 30 giorni dalla consegna. L\'articolo deve essere integro e con i cartellini originali. Il reso è gratuito.',
      },
      {
        question: 'Posso cambiare taglia?',
        answer: 'Sì, puoi richiedere un cambio taglia entro 30 giorni. Contattaci via email o WhatsApp indicando il numero d\'ordine e la nuova taglia desiderata.',
      },
    ],
  },
];

export default function FAQ() {
  return (
    <div className="bg-ivory pt-32 min-h-screen" data-testid="faq-page">
      {/* Header */}
      <section className="pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto text-center">
          <motion.p
            className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Supporto
          </motion.p>
          <motion.h1
            className="font-heading text-5xl md:text-6xl text-night tracking-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="page-title"
          >
            FAQ
          </motion.h1>
          <motion.p
            className="font-body text-lg text-stone-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Trova le risposte alle domande più frequenti
          </motion.p>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto space-y-12">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: categoryIndex * 0.1 }}
            >
              <h2 className="font-heading text-2xl text-night mb-6">{category.title}</h2>
              <Accordion type="single" collapsible className="space-y-2">
                {category.items.map((item) => (
                  <AccordionItem
                    key={item.question}
                    value={item.question}
                    className="bg-white px-6 border-none"
                  >
                    <AccordionTrigger className="font-body text-night hover:text-champagne text-left">
                      {item.question}
                    </AccordionTrigger>
                    <AccordionContent className="font-body text-stone-600">
                      {item.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-stone-100">
        <div className="max-w-xl mx-auto px-6 text-center">
          <h2 className="font-heading text-3xl text-night mb-4">
            Non hai trovato la risposta?
          </h2>
          <p className="font-body text-stone-500 mb-8">
            Il nostro team è pronto ad aiutarti. Contattaci via WhatsApp per una risposta immediata.
          </p>
          <Button
            asChild
            className="bg-green-500 text-white hover:bg-green-600 rounded-full px-8 py-6 text-sm uppercase tracking-widest"
            data-testid="whatsapp-cta"
          >
            <a
              href="https://wa.me/393514504629?text=Ciao!%20Ho%20una%20domanda%20su%20La%20Villa"
              target="_blank"
              rel="noopener noreferrer"
            >
              <MessageCircle className="mr-2" size={18} />
              Scrivici su WhatsApp
            </a>
          </Button>
        </div>
      </section>

      {/* Bottom padding for mobile CTA */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
